import { useRef } from "react";
import { createRoot } from "react-dom/client";
import { v4 as uuidv4 } from "uuid";
import { Logger } from "../../../lib/logger/Logger";
import { drawDOM, exportPDF } from "@progress/kendo-drawing";
import { uploadFileWithProvidedPath } from "./useStorage";
import { ErrorCategoryTag } from "../../../lib/logger/Logger";
import { base64ToBlob } from "../lib/utils/utils-functions";

export interface OperationResult {
	success: boolean;
	error: string | null;
	blob: Blob | null;
}

// Define PaperSize type to match Kendo's requirements
type PaperSize = [string | number, string | number] | string;

interface ExportPdfProps {
	documentComponent: React.ReactNode;
	filePath?: string;
	paperSize?: PaperSize; // Optional custom paper size
	scale?: number; // Optional custom scale
	pageTemplate?: ({
		element,
		pageNum,
		totalPages,
	}: {
		element: HTMLElement;
		pageNum: number;
		totalPages: number;
	}) => string; // Optional page template function returning HTML string
	customFooterHeight?: number; // Height in mm for bottom margin adjustment
	customHeaderHeight?: number; // Height in mm for top margin adjustment (for page headers)
	forcePageBreak?: string; // Optional CSS selector that forces a page break before matching elements
}

export const useExportPdf = () => {
	const pdfRef = useRef(null);

	/**
	 * Waits for a React element to be rendered in the DOM using multiple detection strategies.
	 *
	 * This function is crucial for PDF generation where we need to ensure the React component
	 * is fully rendered before attempting to convert it to PDF. React rendering is asynchronous,
	 * so we can't rely on the component being immediately available after calling root.render().
	 *
	 * The function uses a multi-layered approach to detect element availability:
	 * 1. Immediate check - if element is already available
	 * 2. MutationObserver - watches for DOM changes
	 * 3. Progressive polling - uses requestAnimationFrame for continuous checking
	 * 4. Safety timeout - prevents infinite waiting
	 *
	 * @param refObject - React ref that should point to the target element
	 * @param container - DOM container where the element is being rendered
	 * @param maxWaitMs - Maximum time to wait in milliseconds (default: 15000ms = 15 seconds)
	 * @returns Promise<boolean> - true if element was found, false if timeout occurred
	 */
	const waitForElement = async (
		refObject: React.RefObject<any>,
		container: HTMLElement,
		maxWaitMs: number = 15000
	): Promise<boolean> => {
		const startTime = Date.now();
		let detectionMethod = "unknown";

		return new Promise((resolve) => {
			let resolved = false;

			const resolveOnce = (success: boolean, method: string) => {
				if (!resolved) {
					resolved = true;
					detectionMethod = method;
					resolve(success);
				}
			};

			// Strategy 1: Immediate check - element might already be available
			// This handles cases where React renders synchronously or very quickly
			if (refObject.current) {
				resolveOnce(true, "immediate-check");
				return;
			}

			// Strategy 2: MutationObserver - watches for DOM changes
			// This is the most efficient method as it only triggers when the DOM actually changes
			const observer = new MutationObserver((mutations) => {
				if (refObject.current) {
					observer.disconnect();
					resolveOnce(true, "MutationObserver");
				}
			});

			// Configure observer to watch for child additions/removals in the entire subtree
			observer.observe(container, {
				childList: true,
				subtree: true,
				attributes: false,
			});

			// Strategy 3: Progressive polling with requestAnimationFrame
			// This provides a fallback in case MutationObserver misses the change
			// requestAnimationFrame ensures we check at optimal times (before next paint)
			let rafCallCount = 0;
			const pollWithRaf = () => {
				rafCallCount++;

				// Check if we've exceeded the maximum wait time
				if (Date.now() - startTime > maxWaitMs) {
					observer.disconnect();
					resolveOnce(false, "timeout");
					return;
				}

				// Check if element is now available
				if (refObject.current) {
					observer.disconnect();
					resolveOnce(
						true,
						`requestAnimationFrame (${rafCallCount} calls)`
					);
					return;
				}

				// Schedule next check for the next animation frame
				// This is more efficient than setInterval as it syncs with browser rendering
				requestAnimationFrame(pollWithRaf);
			};

			// Start the polling process
			pollWithRaf();

			// Strategy 4: Safety timeout - absolute fallback
			// This ensures we never wait indefinitely, even if all other strategies fail
			setTimeout(() => {
				observer.disconnect();
				if (!resolved) {
					resolveOnce(false, "safety-timeout");
				}
			}, maxWaitMs);
		});
	};

	/**
	 * Takes a React component and exports it as a PDF
	 * Returns the path to the PDF file in Supabase storage
	 */
	const exportPdf = async ({
		documentComponent,
		filePath,
		paperSize = ["210mm", "297mm"] as [string, string], // Default A4 size
		scale = 0.75, // Default scale
		pageTemplate, // Optional page template
		customFooterHeight = 0, // Default no extra margin
		customHeaderHeight = 0, // Default no extra top margin
		forcePageBreak,
	}: ExportPdfProps): Promise<OperationResult> => {
		const uuid = uuidv4();

		Logger.info(`Start processing PDF export ${uuid}`, {
			documentComponent,
			paperSize,
			scale,
		});

		const pdfNode = (
			<div
				style={{
					// Make sure the element is not visible between rendering and exporting
					position: "absolute",
					left: "-10000px",
					display: undefined,
				}}
				key={uuid}
			>
				<div ref={pdfRef} className="pdf">
					{documentComponent}
				</div>
			</div>
		);

		// Create a container for the PDF wrapper
		const container = document.createElement("div");
		document.body.appendChild(container);

		const root = createRoot(container);
		root.render(pdfNode);

		// check if the root node has rendered
		if (!root) {
			Logger.error(
				"Root node not rendered",
				{ category: ErrorCategoryTag.PDF },
				{ root }
			);
			document.body.removeChild(container);
			return {
				success: false,
				error: "PDF konnte in diesem Browser nicht erstellt werden.",
				blob: null,
			};
		}

		// Wait for the PDF component to be rendered (NEW OPTIMIZED METHOD)
		const renderStartTime = Date.now();
		Logger.info("Waiting for PDF to be rendered");

		const isRendered = await waitForElement(pdfRef, container, 15000);
		const renderTime = Date.now() - renderStartTime;

		Logger.info(`PDF rendering completed in ${renderTime}ms`, {
			category: ErrorCategoryTag.PDF,
			renderTime,
			success: isRendered,
		});

		if (!isRendered) {
			root.unmount();
			document.body.removeChild(container);
			Logger.error("PDF not rendered after 15 seconds", {
				category: ErrorCategoryTag.PDF,
			});
			return {
				success: false,
				error: "PDF konnte nicht innerhalb von 15 Sekunden erstellt werden.",
				blob: null,
			};
		}

		Logger.info(`Start saving PDF export ${uuid}, ${pdfRef.current}`);

		// get the element with className "pdf"
		const pdfElement = document.querySelector(".pdf") as HTMLElement;

		if (!pdfElement) {
			Logger.error(
				"PDF element not found",
				{ category: ErrorCategoryTag.PDF },
				{ pdfElement }
			);
			root.unmount();
			document.body.removeChild(container);
			return {
				success: false,
				error: "PDF Element konnte nicht gefunden werden.",
				blob: null,
			};
		}

		// Prepare drawDOM options with optional page template and margin adjustment
		const drawOptions: any = {
			paperSize: paperSize,
			keepTogether: "p",
			scale: scale,
			repeatHeaders: true,
		};

		// Add page template if provided
		if (pageTemplate) {
			drawOptions.template = pageTemplate;
		}

		if (forcePageBreak) {
			drawOptions.forcePageBreak = forcePageBreak;
		}

		// Adjust margins if custom header/footer is used
		if (customFooterHeight > 0 || customHeaderHeight > 0) {
			drawOptions.margin = {
				...(customFooterHeight > 0 && {
					bottom: `${customFooterHeight + 5}mm`,
				}),
				...(customHeaderHeight > 0 && {
					top: `${customHeaderHeight + 5}mm`,
				}),
			};
		}

		const group = await drawDOM(pdfElement, drawOptions);

		if (!group) {
			Logger.error(
				"Error drawing PDF",
				{
					category: ErrorCategoryTag.PDF,
				},
				{ group }
			);
			root.unmount();
			document.body.removeChild(container);
			return {
				success: false,
				error: "Fehler beim Erstellen des Dokuments (drawDOM)",
				blob: null,
			};
		}

		const dataUri = await exportPDF(group, {});
		if (!dataUri) {
			Logger.error(
				"Error exporting PDF",
				{
					category: ErrorCategoryTag.PDF,
				},
				{ dataUri }
			);
			root.unmount();
			document.body.removeChild(container);
			return {
				success: false,
				error: "Fehler beim Erstellen des Dokuments (exportPDF)",
				blob: null,
			};
		}

		const pdfBlob = base64ToBlob(dataUri, "application/pdf");

		// Skip upload if no filePath provided (e.g., for ZAK export)
		if (!filePath || filePath.trim() === "") {
			Logger.info(`Finished processing PDF export ${uuid} (blob only)`);
			root.unmount();
			document.body.removeChild(container);
			return { success: true, error: null, blob: pdfBlob };
		}

		try {
			const { success, pathName } = await uploadFileWithProvidedPath({
				filePath,
				fileBody: pdfBlob,
				fileOptions: {
					contentType: "application/pdf",
				},
			});

			if (!success) {
				Logger.error(
					"Error uploading PDF",
					{ category: ErrorCategoryTag.PDF },
					{ pathName }
				);
				root.unmount();
				document.body.removeChild(container);
				return {
					success: false,
					error: `Fehler beim Speichern des Dokuments (${pathName})`,
					blob: null,
				};
			}
		} catch (error) {
			Logger.error(
				error,
				{ category: ErrorCategoryTag.PDF },
				"Error uploading PDF"
			);
			root.unmount();
			document.body.removeChild(container);
			return {
				success: false,
				error: "Fehler beim Speichern des Dokuments",
				blob: null,
			};
		}

		Logger.info(`Finished processing PDF export ${uuid}`);
		root.unmount();
		document.body.removeChild(container);

		return { success: true, error: null, blob: pdfBlob };
	};

	return {
		exportPdf,
	};
};
